// Единая точка авторизованных вызовов из админ-панели.
// OAuth находится на отдельном домене: относительный /api здесь использовать нельзя.
import { clearTokens, ensureFreshAccess, getTokens } from '../utils/authFetch';

const OAUTH_API = 'https://oauth.eight-percent.com/api/accounts';

function errorMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload.detail === 'string') return payload.detail;
  if (typeof payload.message === 'string') return payload.message;
  if (payload.errors && typeof payload.errors === 'object') {
    const first = Object.values(payload.errors).flat()[0];
    if (first) return String(first);
  }
  return fallback;
}

export async function authFetch(url, options = {}) {
  const hasFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const perform = async (access) => fetch(url, {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.body && !hasFormData ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${access}`,
      ...options.headers,
    },
  });

  let { access } = getTokens();
  if (!access) throw new Error('Требуется авторизация');

  let response = await perform(access);
  if (response.status === 401) {
    try {
      access = await ensureFreshAccess();
      response = await perform(access);
    } catch {
      clearTokens();
      throw new Error('Сессия истекла. Войдите снова через OAuth.');
    }
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(errorMessage(payload, `Ошибка запроса: ${response.status}`));
  }

  if (response.status === 204) return null;
  return response.json();
}

function query(params = {}) {
  const values = Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined);
  return values.length ? `?${new URLSearchParams(values).toString()}` : '';
}

export const adminAPI = {
  getUsers: (params = {}) => authFetch(`${OAUTH_API}/admin/users/${query(params)}`),
  createUser: (data) => authFetch(`${OAUTH_API}/admin/users/`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  updateUser: (userId, data) => authFetch(`${OAUTH_API}/admin/users/${userId}/`, {
    method: 'PATCH', body: JSON.stringify(data),
  }),
  deleteUser: (userId) => authFetch(`${OAUTH_API}/admin/users/${userId}/`, { method: 'DELETE' }),
  sendRecovery: (userId) => authFetch(`${OAUTH_API}/admin/users/${userId}/send-recovery/`, {
    method: 'POST', body: JSON.stringify({}),
  }),

  // Следующие маршруты появятся вместе с соответствующими модулями панели.
  getStats: () => authFetch(`${OAUTH_API}/admin/stats/`),
  getServices: () => authFetch(`${OAUTH_API}/admin/services/`),
  getServicesStatus: () => authFetch(`${OAUTH_API}/admin/services/`),
  restartService: (serviceName) => authFetch(`${OAUTH_API}/admin/services/${serviceName}/restart/`, { method: 'POST' }),
  manageService: ({ serviceName, action, data = {} }) => authFetch(`${OAUTH_API}/admin/services/${serviceName}/${action}/`, { method: 'POST', body: JSON.stringify(data) }),
  getAIMetrics: () => authFetch(`${OAUTH_API}/admin/ai-metrics/`),
  getAITasks: (params = {}) => authFetch(`${OAUTH_API}/admin/ai-tasks/${query(params)}`),
  manageAI: ({ action, data = {} }) => authFetch(`${OAUTH_API}/admin/ai/${action}/`, { method: 'POST', body: JSON.stringify(data) }),
};

export const authAPI = {
  login: async (login, password) => {
    const response = await fetch(`${OAUTH_API}/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ login, password }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(errorMessage(payload, 'Ошибка входа'));
    return payload;
  },
  verifyToken: () => authFetch(`${OAUTH_API}/profile/check-token/`),
};
